import React, { useState, useEffect } from 'react';

interface ToggleProps {
  /** Current value of the toggle (true/false) */
  value?: boolean;
  /** Called whenever the toggle changes */
  onChange?: (value: boolean) => void;
  /** Optional: disable the toggle */
  disabled?: boolean;
  /** Optional: label displayed next to toggle */
  label?: string;
  /** Optional: used for API loading state */
  isLoading?: boolean;
  /** Optional: custom class names for outer wrapper */
  className?: string;
}

export const Toggle: React.FC<ToggleProps> = ({
  value,
  onChange,
  disabled = false,
  label,
  isLoading = false,
  className = '',
}) => {
  const [enabled, setEnabled] = useState(value ?? false);

  useEffect(() => {
    if (value !== undefined) setEnabled(value);
  }, [value]);

  const handleToggle = () => {
    if (disabled || isLoading) return;
    const newValue = !enabled;
    setEnabled(newValue);
    onChange?.(newValue);
  };

  return (
    <div className={`flex items-center justify-between gap-3 ${className}`}>
      {label && (
        <p className="text-text-primary text-[16px] md:text-xl">
          {label}
        </p>
      )}

      <div className="">
        <button
          type="button"
          disabled={disabled || isLoading}
          onClick={handleToggle}
          className={`relative inline-flex w-[64px] !min-h-[32px] md:h-[32px] p-[2px] items-center rounded-full transition-colors duration-300 ${
            enabled ? 'bg-[#24C661]' : 'bg-gray-300'
          } ${disabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
        >
          <span
            className={`inline-block h-[28px] w-[28px] transform rounded-full bg-white transition-transform duration-300 ${
              enabled ? 'translate-x-[32px]' : 'translate-x-0'
            }`}
          />
          {isLoading && (
            <span className="absolute inset-0 flex items-center justify-center text-white text-xs">
              ...
            </span>
          )}
        </button>
      </div>
    </div>
  );
};
